import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClientRouteHost } from '../context/ClientRouteHostContext'
import { CircularProgress, Alert, Dialog, DialogContent, DialogActions, useMediaQuery } from '@mui/material'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import {
  PrivateLessonPackage,
  Client,
  PaymentMethod,
  PrivateLessonInstructor
} from '../../../pure-js/types/GrooverTypes'
import { usePrivateLessonConfig } from '../hooks/usePrivateLessonConfig'
import { usePrivateLessonBlocks } from '../hooks/usePrivateLessonBlocks'
import { getClient, getStripeConfig } from '../libs/DBApiHandler'
import { isPaymentMethodEnabled } from '../../../pure-js/libs/PaymentHelper'
import { getSwishErrorTextKey, isKnownSwishErrorCode } from '../../../pure-js/libs/SwishErrorMessages'
import {
  createPrivateLessonOrder,
  createSwishPaymentIntentForPrivateLessonOrder,
  createStripePaymentIntentForPrivateLessonOrder,
  cancelSwishPaymentIntent,
  getOrCreateUser,
  sendPrivateLessonsPaymentSuccessEmail
} from '../libs/CloudFunctionsApiHandler'
import { Elements } from '@stripe/react-stripe-js'
import getStripe from '../libs/getStripe'
import StripeCheckoutForm from '../components/StripeCheckoutForm'
import { StripeConfig, StripePaymentIntentStatusValue } from '../../../pure-js/types/StripeTypes'
import { SwishPaymentStatus } from '../../../pure-js/types/SwishTypes'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { getInternationalPhoneNumber, getPhoneForSwish } from '../../../pure-js/libs/PhoneHelper'
import { useSwishPaymentIntentStatus, useStripePaymentIntentStatus } from '../libs/DBApiHandler'
import Box from '../components/Box'
import GrooverText from '../components/GrooverText'
import { getGrooverText, getGrooverTextStyle, formatGrooverText } from '../libs/GrooverTextRepository'
import { _getLanguageCode } from '../hooks/useLanguage'
import useDeviceDetect from '../hooks/useDeviceDetect'
import swishIcon from '/public/swishIcon.svg'
import swishBigLogo from '/public/swishBigLogo.svg'
import QRCodeElement from '../components/QRCodeElement'
import swedishAppStoreBadge from '/Download_on_the_App_Store_Badge_SE_RGB_blk_100317.svg'
import englishAppStoreBadge from '/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg'
import { Texts } from '../../../pure-js/libs/Texts'
import PrivateLessonsStepHeader from '../components/PrivateLessonsStepHeader'
import PrivateLessonInstructorCard from '../components/PrivateLessonInstructorCard'
import PrivateLessonCalendar from '../components/PrivateLessonCalendar'
import PrivateLessonTimeSlots from '../components/PrivateLessonTimeSlots'
import { PhoneInputWithFlag } from '../components/PhoneInputWithFlag'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { DEFAULT_CURRENCY, getCurrencyForClient } from '../../../pure-js/libs/currencyHelper'

dayjs.extend(isBetween)
import useAppState from '../hooks/useAppState'
import { Language } from '../../../pure-js/types/Antiloop'
import PrivateLessonsLanguageToggle from '../components/PrivateLessonsLanguageToggle'

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

const ValidityInfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  padding: 12px 16px;
  margin-top: 8px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #9a3412;
`

const CheckoutErrorMessage: React.FC<{ fieldState: any; name?: string }> = ({ fieldState }) => {
  if (!fieldState?.error?.message) return null
  return <ErrorMessage>{fieldState.error.message}</ErrorMessage>
}

/**
 * PrivateLessonsCheckoutPage component for purchasing private lesson packages
 *
 * URL: /privateLessons/:clientId
 *
 * Multi-step flow:
 * Step 1: Select Package
 * Step 2: Select Instructor
 * Step 3: Enter Information & Book First Lesson (optional)
 * Step 4: Pay
 */

/** When false, the "Book Your First Lesson" option is hidden; customers schedule after purchase. Set to true to re-enable. */
const BOOK_FIRST_LESSON_AT_CHECKOUT_ENABLED = false

interface UserInfo {
  name: string
  phoneNumber: string
  email: string
  message?: string
}

interface FirstLessonBooking {
  date: string | null
  time: string | null
  instructorId?: string
}

export const PrivateLessonsCheckoutPage: React.FC = () => {
  const { clientId } = useClientRouteHost()
  const { state } = useAppState()
  const selectedLanguage: Language = state.selectedLanguage
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1)
  const [isProcessing, setIsProcessing] = useState(false)
  const [swishInstructionUUID, setSwishInstructionUUID] = useState('')
  const [swishPaymentIntentStatus, setSwishPaymentIntentStatus] = useState<SwishPaymentStatus>(
    SwishPaymentStatus.PENDING
  )
  const [swishErrorCode, setSwishErrorCode] = useState<string | null>(null)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [selectedPackage, setSelectedPackage] = useState<PrivateLessonPackage | null>(null)
  const [selectedInstructor, setSelectedInstructor] = useState<string | 'all' | null>('all')
  const [client, setClient] = useState<Client | null>(null)
  const [userInfo, setUserInfo] = useState<UserInfo>({
    name: '',
    phoneNumber: '',
    email: '',
    message: ''
  })
  const [honeypot, setHoneypot] = useState('')
  const [firstLessonBooking, setFirstLessonBooking] = useState<FirstLessonBooking | null>(null)
  const [skipBooking, setSkipBooking] = useState(!BOOK_FIRST_LESSON_AT_CHECKOUT_ENABLED)
  const [validationErrors, setValidationErrors] = useState<{
    email?: string
    phoneNumber?: string
    name?: string
    swishPhoneNumber?: string
  }>({})
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [paymentFailed, setPaymentFailed] = useState(false)
  const [paymentFailedMessage, setPaymentFailedMessage] = useState<string | null>(null)
  const [paymentAbortInProgress, setPaymentAbortInProgress] = useState(false)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [hasSentPaymentSuccessEmail, setHasSentPaymentSuccessEmail] = useState(false)
  const [swishPhoneNumber, setSwishPhoneNumber] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<'swish' | 'stripe'>('swish')
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null)
  const [stripeConfig, setStripeConfig] = useState<StripeConfig | null>(null)
  const [stripePostPaymentLoading, setStripePostPaymentLoading] = useState(false)
  /** Kept after clientSecret is cleared post-payment so Firestore can still listen to stripe_payment_intent_status/{pi_…} */
  const [stripePaymentIntentIdPersisted, setStripePaymentIntentIdPersisted] = useState<string | null>(null)

  // Extract paymentIntentId from clientSecret format: "pi_XXXXX_secret_YYYYY"
  const stripePaymentIntentIdFromSecret = useMemo(
    () => stripeClientSecret?.split('_secret_')[0] ?? null,
    [stripeClientSecret]
  )
  const stripePaymentIntentId = stripePaymentIntentIdFromSecret ?? stripePaymentIntentIdPersisted
  const stripePaymentIntentStatus = useStripePaymentIntentStatus(stripePaymentIntentId)

  const stripePostPaymentResolvedRef = useRef(false)
  const stripePostPaymentFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearStripePostPaymentFallbackTimer = useCallback(() => {
    if (stripePostPaymentFallbackTimerRef.current != null) {
      clearTimeout(stripePostPaymentFallbackTimerRef.current)
      stripePostPaymentFallbackTimerRef.current = null
    }
  }, [])

  const completeStripePostPaymentSuccess = useCallback(() => {
    if (stripePostPaymentResolvedRef.current) return
    stripePostPaymentResolvedRef.current = true
    clearStripePostPaymentFallbackTimer()
    setStripePaymentIntentIdPersisted(null)
    setStripePostPaymentLoading(false)
    setPaymentSuccess(true)
  }, [clearStripePostPaymentFallbackTimer])

  const { config, loading, error } = usePrivateLessonConfig(clientId || null)
  const { data: blocks = [] } = usePrivateLessonBlocks(clientId || null)
  const packages = config?.packages?.length ? config.packages : client?.privateLessonPackages || []
  const instructors: PrivateLessonInstructor[] = config?.instructors || []
  const currency = config?.currency || (client ? getCurrencyForClient(client) : DEFAULT_CURRENCY)
  const showAppDownloadSection = client?.featureToggles?.registrationAppDownload === true && currency === 'SEK'
  const validityMonths = config?.validityMonths ?? client?.privateLessonValidityMonths ?? 12
  const hasPackageData = packages.length > 0
  let swishPaymentIntentMonitor = useSwishPaymentIntentStatus(swishInstructionUUID)
  // Phone-only detection: deep linking into Swish app doesn't make sense on desktop browsers.
  const { isMobile: isPhoneDevice } = useDeviceDetect()
  // Use width-based breakpoint for layout only, to keep desktop UI unchanged.
  const isMobileLayout = useMediaQuery('screen and (max-width: 768px)')

  // Step 2 default: "All instructors"
  useEffect(() => {
    if (currentStep === 2 && !selectedInstructor) {
      setSelectedInstructor('all')
    }
  }, [currentStep, selectedInstructor])

  // On mobile, ensure step 3 starts at the top (avoid landing mid-scroll at the calendar)
  useEffect(() => {
    if (!isMobileLayout) return
    if (currentStep !== 3) return

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'auto' })
    })
  }, [currentStep, isMobileLayout])

  // On mobile, ensure the payment success view starts at the top
  useEffect(() => {
    if (!isMobileLayout) return
    if (!paymentSuccess) return

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'auto' })
    })
  }, [isMobileLayout, paymentSuccess])

  // Filter only active and non-deleted packages
  const activePackages = packages.filter((pkg) => pkg.active !== false && !pkg.isDeleted)

  // Calculate blocked times for the selected date
  const blockedTimesForDate = useMemo(() => {
    if (!firstLessonBooking?.date || !selectedPackage) return []

    const selectedDay = dayjs(firstLessonBooking.date)
    const lessonDuration = selectedPackage.durationMin

    return blocks
      .filter((block) => {
        const blockStart = dayjs(block.startDate)
        const blockEnd = dayjs(block.endDate)

        // Check if block overlaps with selected date
        return selectedDay.isSame(blockStart, 'day') || selectedDay.isBetween(blockStart, blockEnd, 'day', '[]')
      })
      .flatMap((block) => {
        // Generate all time slots that overlap with this block
        const blockStart = dayjs(block.startDate)
        const blockEnd = dayjs(block.endDate)
        const blockedSlots: string[] = []

        // Generate time slots (09:00, 11:00, 13:00, 15:00, 17:00, 19:00)
        for (let hour = 9; hour <= 19; hour += 2) {
          const slotTime = selectedDay.hour(hour).minute(0).second(0)
          const slotEnd = slotTime.add(lessonDuration, 'minute')

          // Check if this slot overlaps with the block
          if (
            slotTime.isBetween(blockStart, blockEnd, null, '[)') ||
            slotEnd.isBetween(blockStart, blockEnd, null, '(]') ||
            (slotTime.isBefore(blockStart) && slotEnd.isAfter(blockEnd))
          ) {
            blockedSlots.push(slotTime.format('HH:mm'))
          }
        }

        return blockedSlots
      })
  }, [blocks, firstLessonBooking?.date, selectedPackage])

  useEffect(() => {
    if (!paymentSuccess || hasSentPaymentSuccessEmail) return
    if (!clientId || !orderId) return
    if (!userInfo.email || !userInfo.name) return

    setHasSentPaymentSuccessEmail(true)

    sendPrivateLessonsPaymentSuccessEmail({
      clientId,
      userEmail: userInfo.email,
      userName: userInfo.name,
      registerToken: orderId
    }).catch((err) => {
      console.error('Failed to send private lessons payment success email', err)
    })
  }, [clientId, hasSentPaymentSuccessEmail, orderId, paymentSuccess, userInfo.email, userInfo.name])

  // Auto-select package when packages are loaded
  useEffect(() => {
    if (activePackages && activePackages.length > 0 && !selectedPackage) {
      const popularPackage = activePackages.find((pkg) => pkg.isPopular)
      setSelectedPackage(popularPackage || activePackages[0])
    }
  }, [activePackages, selectedPackage])

  // Fetch client data for banner
  useEffect(() => {
    let isMounted = true

    const fetchClient = async () => {
      if (clientId) {
        try {
          const clientData = await getClient(clientId)
          if (isMounted && clientData) {
            setClient(clientData)
          }
        } catch (error) {
          if (isMounted) {
            console.error('Failed to fetch client data:', error)
          }
        }
      }
    }
    fetchClient()

    return () => {
      isMounted = false
    }
  }, [clientId])

  // React to Stripe payment intent status (requires persisted PI id after clientSecret is cleared)
  useEffect(() => {
    if (!stripePostPaymentLoading || !stripePaymentIntentIdPersisted) return
    if (!stripePaymentIntentStatus?.data) return
    const { status, errorMessage } = stripePaymentIntentStatus.data as {
      status: StripePaymentIntentStatusValue
      errorMessage?: string
    }
    if (status === StripePaymentIntentStatusValue.Succeeded) {
      completeStripePostPaymentSuccess()
    } else if (status === StripePaymentIntentStatusValue.Failed) {
      stripePostPaymentResolvedRef.current = true
      clearStripePostPaymentFallbackTimer()
      setStripePaymentIntentIdPersisted(null)
      setStripePostPaymentLoading(false)
      setPaymentFailed(true)
      setPaymentFailedMessage(errorMessage ?? null)
    }
  }, [
    stripePaymentIntentStatus?.data,
    stripePostPaymentLoading,
    stripePaymentIntentIdPersisted,
    completeStripePostPaymentSuccess,
    clearStripePostPaymentFallbackTimer
  ])

  useEffect(() => {
    if (!swishPaymentIntentMonitor) return
    if (swishPaymentIntentMonitor.status == 'success') {
      // @ts-ignore
      const { status, errorCode } = swishPaymentIntentMonitor.data
      if (status) {
        setSwishPaymentIntentStatus(status)
      }
      if (errorCode) {
        setSwishErrorCode(errorCode)
      }
    }
  }, [swishPaymentIntentMonitor.data])

  useEffect(() => {
    console.log('swish payment status', swishPaymentIntentStatus)
    if (swishPaymentIntentStatus == SwishPaymentStatus.PAID) {
      setShowSuccessModal(true)
      setIsProcessing(false)
      setPaymentSuccess(true)
    }
    if (swishPaymentIntentStatus == SwishPaymentStatus.ERROR) {
      setSwishInstructionUUID('')
      setIsProcessing(false)
      setShowSuccessModal(false)
      const errorTextKey = getSwishErrorTextKey(swishErrorCode ?? undefined)
      const errorText = getGrooverText(errorTextKey, selectedLanguage)
      const suffix = swishErrorCode && !isKnownSwishErrorCode(swishErrorCode) ? ` (${swishErrorCode})` : ''
      setPaymentFailedMessage(errorText + suffix)
      setPaymentFailed(true)
    }
    if (
      [SwishPaymentStatus.CANCELLED, SwishPaymentStatus.DECLINED].includes(
        swishPaymentIntentStatus as SwishPaymentStatus
      )
    ) {
      setSwishInstructionUUID('')
      setIsProcessing(false)
      setShowSuccessModal(false)
      setPaymentFailedMessage(
        swishPaymentIntentStatus === SwishPaymentStatus.CANCELLED
          ? getGrooverText('swishPaymentCancelledMessage', selectedLanguage)
          : getGrooverText('swishPaymentDeclinedMessage', selectedLanguage)
      )
      setPaymentFailed(true)
    }
  }, [swishPaymentIntentStatus, swishErrorCode, selectedLanguage])

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false)
    setSelectedPackage(null)
  }

  /**
   * MUI fires `onClose` on backdrop click and Escape. Dismissing the dialog while the Swish
   * request is still open left it live at Swish for its full ~3 min validity, so the customer's
   * next attempt was rejected with RP06 and left another unpaid order behind (COU-333).
   */
  const handleDismissSwishModal = () => {
    // Guard on the UUID too: `handleCancelSwishPayment` no-ops without one, which would leave the
    // dialog stuck open with no way out.
    if (
      swishInstructionUUID &&
      [SwishPaymentStatus.PENDING, SwishPaymentStatus.CREATED].includes(swishPaymentIntentStatus)
    ) {
      void handleCancelSwishPayment()
      return
    }
    handleCloseSuccessModal()
  }

  const handleCloseSuccessPage = () => {
    clearStripePostPaymentFallbackTimer()
    stripePostPaymentResolvedRef.current = false
    setStripePaymentIntentIdPersisted(null)
    setPaymentSuccess(false)
    setOrderId(null)
    setSelectedPackage(null)
    setSelectedInstructor('all')
    setFirstLessonBooking(null)
    setSkipBooking(!BOOK_FIRST_LESSON_AT_CHECKOUT_ENABLED)
    setUserInfo({ name: '', phoneNumber: '', email: '', message: '' })
    setValidationErrors({})
    setCurrentStep(1)
  }

  const handleOpenSwishApp = () => {
    const swishUrl = 'swish://'
    const fallbackUrl = 'https://swish.nu'

    const link = document.createElement('a')
    link.href = swishUrl
    link.target = '_blank'
    link.rel = 'noopener noreferrer'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    setTimeout(() => {
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
    }, 1000)
  }

  const validateEmail = (email: string): string | undefined => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) return getGrooverText('validationEmailRequired', selectedLanguage)
    if (!emailRegex.test(email)) return getGrooverText('validationEmailInvalid', selectedLanguage)
    return undefined
  }

  const validatePhoneNumber = (phone: string): string | undefined => {
    const phoneRegex = /^[\+]?[0-9\s\-\(\)]{8,}$/
    if (!phone) return getGrooverText('validationPhoneRequired', selectedLanguage)
    if (!phoneRegex.test(phone)) return getGrooverText('validationPhoneInvalid', selectedLanguage)
    return undefined
  }

  const formatTextWithPlaceholders = (text: string, params: any[]): string => {
    if (!params || params.length === 0) return text
    let formatted = text
    params.forEach((param, i) => {
      const placeholder = new RegExp(`\\{${i}\\}`, 'g')
      formatted = formatted.replace(placeholder, param?.toString() || '')
    })
    return formatted
  }

  const DynamicText = ({ textKey, params, styleKey }: { textKey: string; params: any[]; styleKey: string }) => {
    const text = formatTextWithPlaceholders(getGrooverText(textKey, selectedLanguage), params)
    const designSystemStyle = getGrooverTextStyle(styleKey)
    return <span style={designSystemStyle}>{text}</span>
  }

  const getPackageTitle = (pkg: PrivateLessonPackage) => {
    if (pkg.name) {
      return <span>{pkg.name}</span>
    }
    if (pkg.lessonCount === 1) {
      return <GrooverText textKey="privateLessonsCheckoutPageSingleLesson" language={selectedLanguage} />
    }
    return <DynamicText textKey="privateLessonsCheckoutPageLessons" params={[pkg.lessonCount]} styleKey="h4-dark" />
  }

  const getPackageDescription = (pkg: PrivateLessonPackage) => {
    const durationText = (() => {
      const durationMin = pkg.durationMin
      if (!durationMin) return ''

      // Always show duration in minutes
      const key = durationMin === 1 ? 'privateLessonsCheckoutDurationMinute' : 'privateLessonsCheckoutDurationMinutes'
      return formatTextWithPlaceholders(getGrooverText(key, selectedLanguage), [durationMin])
    })()
    if (pkg.lessonCount === 1) {
      return (
        <span>
          {formatTextWithPlaceholders(
            getGrooverText('privateLessonsCheckoutPageOneLessonDescription', selectedLanguage) || '1 lesson ({0})',
            [durationText]
          )}
        </span>
      )
    }
    return (
      <span>
        {formatTextWithPlaceholders(
          getGrooverText('privateLessonsCheckoutPageLessonsDescription', selectedLanguage) || '{0} lessons ({1} each)',
          [pkg.lessonCount, durationText]
        )}
      </span>
    )
  }

  const handleUserInfoChange = (field: keyof UserInfo, value: string) => {
    setUserInfo((prev) => ({
      ...prev,
      [field]: value
    }))

    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined
      }))
    }

    if (field === 'email') {
      const error = validateEmail(value)
      setValidationErrors((prev) => ({
        ...prev,
        email: error
      }))
    } else if (field === 'phoneNumber') {
      const error = validatePhoneNumber(value)
      setValidationErrors((prev) => ({
        ...prev,
        phoneNumber: error
      }))
    }
  }

  const handleSwishPhoneNumberChange = (value: string) => {
    setSwishPhoneNumber(value)

    if (validationErrors.swishPhoneNumber) {
      setValidationErrors((prev) => ({
        ...prev,
        swishPhoneNumber: undefined
      }))
    }

    const error = validatePhoneNumber(value)
    setValidationErrors((prev) => ({
      ...prev,
      swishPhoneNumber: error
    }))
  }

  const handleCancelSwishPayment = async () => {
    if (!swishInstructionUUID) return

    setPaymentAbortInProgress(true)
    try {
      await cancelSwishPaymentIntent({ instructionUUID: swishInstructionUUID })
    } catch (error) {
      console.error('Error canceling Swish payment:', error)
    }
    setSwishInstructionUUID('')
    setSwishPaymentIntentStatus(SwishPaymentStatus.PENDING)
    setSwishErrorCode(null)
    setShowSuccessModal(false)
    setIsProcessing(false)
    setPaymentAbortInProgress(false)
  }

  // Step navigation
  const nextStep = () => {
    if (currentStep < 4) {
      // Validate current step before proceeding
      if (currentStep === 1 && !selectedPackage) {
        setPaymentError(
          getGrooverText('privateLessonsCheckoutPackageRequired', selectedLanguage) || 'Please select a package'
        )
        return
      }
      if (currentStep === 2 && !selectedInstructor) {
        setPaymentError(
          getGrooverText('privateLessonsCheckoutInstructorRequired', selectedLanguage) || 'Please select an instructor'
        )
        return
      }
      if (currentStep === 3) {
        const emailError = validateEmail(userInfo.email)
        const phoneError = validatePhoneNumber(userInfo.phoneNumber)
        if (!userInfo.name || emailError || phoneError) {
          setValidationErrors({
            name: !userInfo.name
              ? getGrooverText('validationNameRequired', selectedLanguage) || 'Name is required'
              : undefined,
            email: emailError,
            phoneNumber: phoneError
          })
          setPaymentError(getGrooverText('validationFixErrors', selectedLanguage))
          return
        }
        // If booking is not skipped and selected, validate date and time
        if (!skipBooking && firstLessonBooking && (!firstLessonBooking.date || !firstLessonBooking.time)) {
          setPaymentError(
            getGrooverText('privateLessonsCheckoutBookingIncomplete', selectedLanguage) ||
              'Please select both date and time for your first lesson'
          )
          return
        }

        // Initialize Swish phone number from user info
        setSwishPhoneNumber(userInfo.phoneNumber)
      }
      setPaymentError(null)
      // Step 2 (instructor selection) is hidden; jump directly from step 1 to step 3
      const nextStepNumber = currentStep === 1 ? 3 : ((currentStep + 1) as 1 | 2 | 3 | 4)
      setCurrentStep(nextStepNumber)
    }
  }

  const previousStep = () => {
    if (currentStep > 1) {
      // Step 2 (instructor selection) is hidden; jump directly from step 3 back to step 1
      const prevStepNumber = currentStep === 3 ? 1 : ((currentStep - 1) as 1 | 2 | 3 | 4)
      setCurrentStep(prevStepNumber)
      setPaymentError(null)
    }
  }

  const goToStep = (step: 1 | 2 | 3 | 4) => {
    // Only allow going back to completed steps
    if (step < currentStep) {
      setCurrentStep(step)
      setPaymentError(null)
    }
  }

  const handleProcessPayment = async () => {
    if (!clientId || !selectedPackage) {
      console.error('Client ID and selected package are required')
      return
    }

    const emailError = validateEmail(userInfo.email)
    const phoneError = validatePhoneNumber(userInfo.phoneNumber)
    const swishPhoneError = validatePhoneNumber(swishPhoneNumber)

    if (!userInfo.name || emailError || phoneError || swishPhoneError) {
      setValidationErrors({
        name: !userInfo.name
          ? getGrooverText('validationNameRequired', selectedLanguage) || 'Name is required'
          : undefined,
        email: emailError,
        phoneNumber: phoneError,
        swishPhoneNumber: swishPhoneError
      })
      if (swishPhoneError) {
        setPaymentError(swishPhoneError)
      } else {
        setPaymentError(getGrooverText('validationFixErrors', selectedLanguage))
      }
      return
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      // Step 0: make sure user is created
      const enrollmentUser = await getOrCreateUser({
        phone: getInternationalPhoneNumber(userInfo.phoneNumber),
        name: userInfo.name,
        email: userInfo.email
      })
      console.log('found user', enrollmentUser.enrollmentUserId)

      const instructorName =
        selectedInstructor && selectedInstructor !== 'all'
          ? instructors.find((instructor) => instructor.id === selectedInstructor)?.name || undefined
          : undefined
      const orderInstructorId = selectedInstructor && selectedInstructor !== 'all' ? selectedInstructor : undefined
      const firstLesson =
        firstLessonBooking?.date && firstLessonBooking?.time
          ? (() => {
              const startDateTime = dayjs(`${firstLessonBooking.date}T${firstLessonBooking.time}`)
              const endDateTime = startDateTime.add(selectedPackage.durationMin, 'minute')
              return {
                startDate: startDateTime.toISOString(),
                endDate: endDateTime.toISOString(),
                instructorId: firstLessonBooking.instructorId || orderInstructorId
              }
            })()
          : undefined

      const orderResponse = await createPrivateLessonOrder({
        clientId,
        packageId: selectedPackage.id,
        enrollmentUserId: enrollmentUser.enrollmentUserId,
        enrollmentUserName: userInfo.name,
        instructorId: orderInstructorId,
        instructorName,
        registrationComment: userInfo.message || undefined,
        paymentMethod: PaymentMethod.Swish,
        firstLesson,
        honeypot
      })

      setOrderId(orderResponse.orderId)

      // Create Swish payment intent
      const swishResponse = await createSwishPaymentIntentForPrivateLessonOrder({
        clientId,
        orderId: orderResponse.orderId,
        payerPhone: getPhoneForSwish(swishPhoneNumber)
      })

      if ('isError' in swishResponse) {
        console.error('Error creating Swish payment intent:', swishResponse)
        const createErrorCode = ('errorCode' in swishResponse ? swishResponse.errorCode : undefined) as
          | string
          | undefined
        const createErrorTextKey = getSwishErrorTextKey(createErrorCode)
        const createErrorText = getGrooverText(createErrorTextKey, selectedLanguage)
        const suffix = createErrorCode && !isKnownSwishErrorCode(createErrorCode) ? ` (${createErrorCode})` : ''
        setPaymentFailedMessage(createErrorText + suffix)
        setPaymentFailed(true)
        setIsProcessing(false)
        return
      }

      // Start monitoring payment status
      setSwishInstructionUUID(swishResponse.instructionUUID)
      setSwishPaymentIntentStatus(SwishPaymentStatus.PENDING)
      setSwishErrorCode(null)
      setShowSuccessModal(true) // Show the Swish waiting modal
      // Keep isProcessing true until payment is confirmed via webhook
    } catch (error) {
      console.error('Error purchasing package:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to process payment'
      setSwishInstructionUUID('')
      setPaymentError(errorMessage)
      setIsProcessing(false)
    }
  }

  const handleStripePayment = async () => {
    if (!clientId || !selectedPackage) return

    const emailError = validateEmail(userInfo.email)
    const phoneError = validatePhoneNumber(userInfo.phoneNumber)
    if (!userInfo.name || emailError || phoneError) {
      setValidationErrors({
        name: !userInfo.name
          ? getGrooverText('validationNameRequired', selectedLanguage) || 'Name is required'
          : undefined,
        email: emailError,
        phoneNumber: phoneError
      })
      setPaymentError(getGrooverText('validationFixErrors', selectedLanguage))
      return
    }

    setIsProcessing(true)
    setPaymentError(null)

    try {
      const enrollmentUser = await getOrCreateUser({
        phone: getInternationalPhoneNumber(userInfo.phoneNumber),
        name: userInfo.name,
        email: userInfo.email
      })

      const instructorName =
        selectedInstructor && selectedInstructor !== 'all'
          ? instructors.find((i) => i.id === selectedInstructor)?.name || undefined
          : undefined
      const orderInstructorId = selectedInstructor && selectedInstructor !== 'all' ? selectedInstructor : undefined

      const orderResponse = await createPrivateLessonOrder({
        clientId,
        packageId: selectedPackage.id,
        enrollmentUserId: enrollmentUser.enrollmentUserId,
        enrollmentUserName: userInfo.name,
        instructorId: orderInstructorId,
        instructorName,
        registrationComment: userInfo.message || undefined,
        paymentMethod: PaymentMethod.Stripe,
        honeypot
      })

      setOrderId(orderResponse.orderId)

      const piResult = await createStripePaymentIntentForPrivateLessonOrder({
        clientId,
        orderId: orderResponse.orderId
      })

      if ('isError' in piResult) throw new Error(piResult.message)

      const config = await getStripeConfig(client!)
      setStripeConfig(config)
      setStripeClientSecret(piResult.clientSecret)
    } catch (error) {
      setPaymentError(error instanceof Error ? error.message : 'Failed to initiate Stripe payment')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleStripeSuccess = () => {
    const paymentIntentId = stripeClientSecret?.split('_secret_')[0] ?? null
    if (paymentIntentId) {
      setStripePaymentIntentIdPersisted(paymentIntentId)
    }
    setStripeClientSecret(null)
    if (!orderId || !clientId) {
      clearStripePostPaymentFallbackTimer()
      setStripePaymentIntentIdPersisted(null)
      setPaymentSuccess(true)
      return
    }
    stripePostPaymentResolvedRef.current = false
    clearStripePostPaymentFallbackTimer()
    setStripePostPaymentLoading(true)
    stripePostPaymentFallbackTimerRef.current = setTimeout(() => {
      completeStripePostPaymentSuccess()
    }, 30000)
  }

  const formatPrice = (priceInCents: number) => {
    return `${(priceInCents / 100).toFixed(0)} ${currency}`
  }

  // Render Step 1: Package Selection
  const renderStep1 = () => (
    <LeftColumn>
      <SectionTitle>
        <GrooverText textKey="privateLessonsCheckoutPageChoosePackage" language={selectedLanguage} />
      </SectionTitle>

      <PackageList>
        {activePackages.map((pkg) => {
          const isSelected = selectedPackage?.id === pkg.id
          const isPopular = pkg.isPopular

          return (
            <PackageOption key={pkg.id} $isSelected={isSelected} onClick={() => setSelectedPackage(pkg)}>
              <PackageInfo>
                <PackageTitle>{getPackageTitle(pkg)}</PackageTitle>
                {isPopular && (
                  <PopularTag>
                    <GrooverText textKey="checkoutPagePopular" language={selectedLanguage} style={{ color: 'white' }} />
                  </PopularTag>
                )}
                <PackageDescription>{getPackageDescription(pkg)}</PackageDescription>
                <PackagePrice>{formatPrice(pkg.totalPrice)}</PackagePrice>
              </PackageInfo>
              <RadioButton $isSelected={isSelected}>{isSelected && <CheckCircleIcon />}</RadioButton>
            </PackageOption>
          )
        })}
      </PackageList>
    </LeftColumn>
  )

  // Render Step 2: Instructor Selection
  const renderStep2 = () => (
    <LeftColumn>
      <SectionTitle>
        <GrooverText textKey="privateLessonsCheckoutSelectInstructor" language={selectedLanguage} />
      </SectionTitle>

      <InstructorList>
        <PrivateLessonInstructorCard
          isAllInstructors={true}
          isSelected={selectedInstructor === 'all'}
          onClick={() => setSelectedInstructor('all')}
          instructor={{ id: 'all', name: '', specialties: [] }}
          language={selectedLanguage}
        />

        {instructors.map((instructor) => (
          <PrivateLessonInstructorCard
            key={instructor.id}
            instructor={instructor}
            isSelected={selectedInstructor === instructor.id}
            onClick={() => setSelectedInstructor(instructor.id)}
            language={selectedLanguage}
          />
        ))}
      </InstructorList>
    </LeftColumn>
  )

  // Render Step 3: User Information & Optional Booking
  const renderStep3 = () => (
    <LeftColumn>
      <SectionTitle>
        <GrooverText textKey="checkoutPageYourDetails" language={selectedLanguage} />
      </SectionTitle>

      <FormField>
        <GrooverText textKey="checkoutPageFirstName" language={selectedLanguage} />
        <StyledInput
          name="name"
          autoComplete="name"
          type="text"
          placeholder={getGrooverText('checkoutPageEnterName', selectedLanguage)}
          value={userInfo.name}
          onChange={(e) => handleUserInfoChange('name', e.target.value)}
          error={!!validationErrors.name}
        />
        {validationErrors.name && <ErrorMessage>{validationErrors.name}</ErrorMessage>}
      </FormField>

      <FormField>
        <GrooverText textKey="checkoutPageEmail" language={selectedLanguage} />
        <StyledInput
          name="email"
          autoComplete="email"
          type="email"
          placeholder={getGrooverText('checkoutPageEnterEmail', selectedLanguage)}
          value={userInfo.email}
          onChange={(e) => handleUserInfoChange('email', e.target.value)}
          error={!!validationErrors.email}
        />
        {validationErrors.email && <ErrorMessage>{validationErrors.email}</ErrorMessage>}
      </FormField>

      <FormField>
        <GrooverText textKey="checkoutPagePhoneNumber" language={selectedLanguage} />
        <PhoneInputWrapper>
          <PhoneInputWithFlag
            name="phoneNumber"
            autoComplete="tel"
            placeholder={getGrooverText('checkoutPageEnterPhone', selectedLanguage)}
            value={userInfo.phoneNumber}
            onChange={(e) => handleUserInfoChange('phoneNumber', e.target.value)}
            fullWidth
            client={client}
            fieldState={validationErrors.phoneNumber ? { error: { message: validationErrors.phoneNumber } } : undefined}
            ErrorComponent={CheckoutErrorMessage}
          />
        </PhoneInputWrapper>
      </FormField>

      <FormField>
        <GrooverText textKey="privateLessonsCheckoutMessageToInstructor" language={selectedLanguage} />
        <StyledTextArea
          placeholder={
            getGrooverText('privateLessonsCheckoutMessagePlaceholder', selectedLanguage) || 'Message to instructor'
          }
          value={userInfo.message || ''}
          onChange={(e) => handleUserInfoChange('message', e.target.value)}
          rows={4}
        />
      </FormField>

      {/* Honeypot field - hidden from users, catches bots. type="hidden" prevents browser autofill */}
      <input type="hidden" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />

      <ValidityInfoBox>
        <InfoOutlinedIcon sx={{ width: 20, height: 20, color: '#9a3412', flexShrink: 0 }} />
        {formatGrooverText(getGrooverText('privateLessonsValidityMonthsInfo', selectedLanguage), [validityMonths])}
      </ValidityInfoBox>

      {/* Optional First Lesson Booking - hidden when BOOK_FIRST_LESSON_AT_CHECKOUT_ENABLED is false */}
      {BOOK_FIRST_LESSON_AT_CHECKOUT_ENABLED && (
        <BookingSection>
          <SectionTitle>
            <GrooverText textKey="privateLessonsCheckoutBookFirstLesson" language={selectedLanguage} />
          </SectionTitle>

          <SkipBookingCheckbox>
            <input
              type="checkbox"
              id="skipBooking"
              checked={skipBooking}
              onChange={(e) => {
                setSkipBooking(e.target.checked)
                if (e.target.checked) {
                  setFirstLessonBooking(null)
                }
              }}
            />
            <label htmlFor="skipBooking">
              {getGrooverText('privateLessonsCheckoutSkipBooking', selectedLanguage) || 'Skip booking for now'}
            </label>
          </SkipBookingCheckbox>

          {!skipBooking && (
            <BookingContent>
              <CalendarWrapper>
                <PrivateLessonCalendar
                  selectedDate={firstLessonBooking?.date || null}
                  onDateSelect={(date) => {
                    setFirstLessonBooking((prev) => ({
                      ...prev,
                      date,
                      time: null // Reset time when date changes
                    }))
                  }}
                  language={selectedLanguage}
                />
              </CalendarWrapper>

              {firstLessonBooking?.date && (
                <TimeSlotsWrapper>
                  <PrivateLessonTimeSlots
                    selectedTime={firstLessonBooking?.time || null}
                    onTimeSelect={(time) => {
                      setFirstLessonBooking((prev) => {
                        if (!prev || !prev.date) return prev
                        return {
                          date: prev.date,
                          time,
                          instructorId:
                            selectedInstructor && selectedInstructor !== 'all' ? selectedInstructor : undefined
                        }
                      })
                    }}
                    blockedTimes={blockedTimesForDate}
                    instructorId={selectedInstructor}
                    selectedDate={firstLessonBooking.date}
                    language={selectedLanguage}
                  />
                </TimeSlotsWrapper>
              )}
            </BookingContent>
          )}
        </BookingSection>
      )}
    </LeftColumn>
  )

  // Render Step 4: Payment
  const renderStep4 = () => (
    <LeftColumn>
      <SectionTitle>
        <GrooverText textKey="privateLessonsCheckoutPayment" language={selectedLanguage} />
      </SectionTitle>

      {/* Payment method toggle - only show when both are configured */}
      {client?.swish && client?.stripe && isPaymentMethodEnabled(PaymentMethod.StripePrivateLesson, client) && (
        <PaymentMethodToggle>
          <PaymentMethodButton $active={paymentMethod === 'swish'} onClick={() => setPaymentMethod('swish')}>
            <img src={swishIcon} width="20px" height="20px" alt="Swish" />
            Swish
          </PaymentMethodButton>
          <PaymentMethodButton $active={paymentMethod === 'stripe'} onClick={() => setPaymentMethod('stripe')}>
            💳 Card
          </PaymentMethodButton>
        </PaymentMethodToggle>
      )}

      {/* Swish payment */}
      {(paymentMethod === 'swish' ||
        !(client?.stripe && isPaymentMethodEnabled(PaymentMethod.StripePrivateLesson, client))) && (
        <SwishPaymentContainer>
          <SwishLogoSingle src={swishBigLogo} alt="Swish" />
          <GrooverText
            textKey="privateLessonsCheckoutPaymentSwishInfo"
            language={selectedLanguage}
            style={{ marginBottom: '16px', display: 'block', textAlign: 'center', color: '#666' }}
          />

          <FormField>
            <GrooverText textKey="checkoutPageSwishNumber" language={selectedLanguage} />
            <PhoneInputWrapper>
              <PhoneInputWithFlag
                name="swishPhoneNumber"
                placeholder={getGrooverText('checkoutPageEnterPhone', selectedLanguage)}
                value={swishPhoneNumber}
                onChange={(e) => handleSwishPhoneNumberChange(e.target.value)}
                fullWidth
                client={client}
                fieldState={
                  validationErrors.swishPhoneNumber
                    ? { error: { message: validationErrors.swishPhoneNumber } }
                    : undefined
                }
                ErrorComponent={CheckoutErrorMessage}
              />
            </PhoneInputWrapper>
          </FormField>
        </SwishPaymentContainer>
      )}

      {/* Stripe payment */}
      {paymentMethod === 'stripe' &&
        client?.stripe &&
        isPaymentMethodEnabled(PaymentMethod.StripePrivateLesson, client) &&
        stripeClientSecret &&
        stripeConfig && (
          <Elements stripe={getStripe(stripeConfig)} options={{ clientSecret: stripeClientSecret }}>
            <StripeCheckoutForm onIsPaid={handleStripeSuccess} />
          </Elements>
        )}

      <ValidityInfoBox>
        <InfoOutlinedIcon sx={{ width: 20, height: 20, color: '#9a3412', flexShrink: 0 }} />
        {formatGrooverText(getGrooverText('privateLessonsValidityMonthsInfo', selectedLanguage), [validityMonths])}
      </ValidityInfoBox>
    </LeftColumn>
  )

  // Render Order Summary (persists across all steps)
  const renderNavigationButtons = () => (
    <>
      {currentStep === 1 && (
        <ContinueButton onClick={nextStep} disabled={!selectedPackage} style={{ marginTop: '20px', width: '100%' }}>
          {getGrooverText('privateLessonsCheckoutContinue', selectedLanguage) || 'Continue'}
        </ContinueButton>
      )}

      {currentStep === 2 && (
        <NavigationButtons style={{ marginTop: '20px' }}>
          <BackButton onClick={previousStep}>
            {getGrooverText('privateLessonsCheckoutBack', selectedLanguage) || 'Back'}
          </BackButton>
          <ContinueButton onClick={nextStep} disabled={!selectedInstructor}>
            {getGrooverText('privateLessonsCheckoutContinue', selectedLanguage) || 'Continue'}
          </ContinueButton>
        </NavigationButtons>
      )}

      {currentStep === 3 && (
        <NavigationButtons style={{ marginTop: '20px' }}>
          <BackButton onClick={previousStep}>
            {getGrooverText('privateLessonsCheckoutBack', selectedLanguage) || 'Back'}
          </BackButton>
          <ContinueButton onClick={nextStep}>
            {getGrooverText('privateLessonsCheckoutContinue', selectedLanguage) || 'Continue'}
          </ContinueButton>
        </NavigationButtons>
      )}

      {currentStep === 4 && (
        <NavigationButtons style={{ marginTop: '20px' }}>
          <BackButton onClick={previousStep}>
            {getGrooverText('privateLessonsCheckoutBack', selectedLanguage) || 'Back'}
          </BackButton>
          {/* Show Swish pay button when swish is selected or stripe not configured, and no stripe form shown */}
          {(paymentMethod === 'swish' ||
            !(client?.stripe && isPaymentMethodEnabled(PaymentMethod.StripePrivateLesson, client))) && (
            <PayButton onClick={handleProcessPayment} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  <GrooverText textKey="checkoutPageProcessing" language={selectedLanguage} />
                </>
              ) : (
                <>
                  <SwishIcon>
                    <img src={swishIcon} width="20px" height="20px" alt="Swish" />
                  </SwishIcon>
                  {getGrooverText('privateLessonsCheckoutPayAndBook', selectedLanguage) || 'Pay & book'}
                </>
              )}
            </PayButton>
          )}
          {/* Show Stripe initiate button when stripe is selected and no client secret yet */}
          {paymentMethod === 'stripe' &&
            client?.stripe &&
            isPaymentMethodEnabled(PaymentMethod.StripePrivateLesson, client) &&
            !stripeClientSecret && (
              <PayButton onClick={handleStripePayment} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <CircularProgress size={20} color="inherit" />
                    <GrooverText textKey="checkoutPageProcessing" language={selectedLanguage} />
                  </>
                ) : (
                  <>💳 {getGrooverText('privateLessonsCheckoutPayAndBook', selectedLanguage) || 'Pay & book'}</>
                )}
              </PayButton>
            )}
        </NavigationButtons>
      )}
    </>
  )

  const renderOrderSummary = () => {
    const selectedInstructorName =
      selectedInstructor === 'all'
        ? getGrooverText('privateLessonsCheckoutAllInstructors', selectedLanguage) || ''
        : selectedInstructor
          ? instructors.find((i) => i.id === selectedInstructor)?.name || ''
          : getGrooverText('privateLessonsCheckoutNotSelected', selectedLanguage) || ''

    return (
      <>
        <OrderSummarySection>
          <SectionTitle>
            <GrooverText textKey="checkoutPageOrderSummary" language={selectedLanguage} />
          </SectionTitle>

          <OrderItemsWrapper>
            <OrderItem>
              <OrderLabel>
                <GrooverText
                  textKey="privateLessonsCheckoutPackage"
                  language={selectedLanguage}
                  style={{ color: '#666' }}
                />
              </OrderLabel>
              <OrderValue>
                {selectedPackage
                  ? getPackageTitle(selectedPackage)
                  : getGrooverText('privateLessonsCheckoutNotSelected', selectedLanguage)}
              </OrderValue>
            </OrderItem>

            {currentStep >= 2 && (
              <OrderItem>
                <OrderLabel>
                  <GrooverText
                    textKey="privateLessonsCheckoutInstructor"
                    language={selectedLanguage}
                    style={{ color: '#666' }}
                  />
                </OrderLabel>
                <OrderValue>{selectedInstructorName}</OrderValue>
              </OrderItem>
            )}

            {currentStep >= 3 && (
              <>
                <OrderItem>
                  <OrderLabel>
                    <GrooverText
                      textKey="privateLessonsCheckoutFirstLesson"
                      language={selectedLanguage}
                      style={{ color: '#666' }}
                    />
                  </OrderLabel>
                  <OrderValue>
                    {firstLessonBooking?.date && firstLessonBooking?.time
                      ? `${firstLessonBooking.date} ${firstLessonBooking.time}`
                      : getGrooverText('privateLessonsCheckoutNotBooked', selectedLanguage) || 'Not booked'}
                  </OrderValue>
                </OrderItem>

                {selectedPackage && (
                  <OrderItem>
                    <OrderLabel>
                      <GrooverText
                        textKey="privateLessonsCheckoutRemainingAfterPurchase"
                        language={selectedLanguage}
                        style={{ color: '#666' }}
                      />
                    </OrderLabel>
                    <OrderValue>{selectedPackage.lessonCount} lektioner</OrderValue>
                  </OrderItem>
                )}

                <OrderItem>
                  <OrderLabel>
                    <GrooverText
                      textKey="privateLessonsOrderSummaryValidity"
                      language={selectedLanguage}
                      style={{ color: '#666' }}
                    />
                  </OrderLabel>
                  <OrderValue>
                    {formatGrooverText(getGrooverText('privateLessonsOrderSummaryValidityValue', selectedLanguage), [
                      validityMonths
                    ])}
                  </OrderValue>
                </OrderItem>
              </>
            )}
          </OrderItemsWrapper>

          <OrderDivider />

          <OrderTotal>
            <OrderLabel>
              <GrooverText textKey="checkoutPageTotal" language={selectedLanguage} style={{ color: '#666' }} />
            </OrderLabel>
            <OrderValue $isTotal>{selectedPackage ? formatPrice(selectedPackage.totalPrice) : '0 SEK'}</OrderValue>
          </OrderTotal>
        </OrderSummarySection>

        {/* Navigation Buttons */}
        {(!isMobileLayout || currentStep === 4) && renderNavigationButtons()}

        {paymentFailed && (
          <PaymentFailedAlert>
            <PaymentFailedIcon>⚠️</PaymentFailedIcon>
            <PaymentFailedContent>
              <PaymentFailedTitle>
                <GrooverText textKey="checkoutPagePaymentFailed" language={selectedLanguage} />
              </PaymentFailedTitle>
              <PaymentFailedMessage>
                {paymentFailedMessage || (
                  <GrooverText textKey="checkoutPagePaymentFailedMessage" language={selectedLanguage} />
                )}{' '}
                <SupportEmailLink href="mailto:support@coursely.se">support@coursely.se</SupportEmailLink>
              </PaymentFailedMessage>
            </PaymentFailedContent>
            <CloseFailedButton
              onClick={() => {
                setPaymentFailed(false)
                setPaymentFailedMessage(null)
              }}
            >
              ×
            </CloseFailedButton>
          </PaymentFailedAlert>
        )}
      </>
    )
  }

  if (loading) {
    return (
      <CheckoutContainer>
        <Box fullWidth align="center" justify="center" style={{ minHeight: '400px' }}>
          <CircularProgress size={60} thickness={4} />
          <GrooverText textKey="checkoutPageLoading" language={selectedLanguage} />
        </Box>
      </CheckoutContainer>
    )
  }

  if (error && !hasPackageData) {
    return (
      <CheckoutContainer>
        <Box fullWidth align="center" style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
          <Alert severity="error">Error loading packages: {error}</Alert>
        </Box>
      </CheckoutContainer>
    )
  }

  if (!activePackages || activePackages.length === 0) {
    return (
      <CheckoutContainer>
        <EmptyStateContainer>
          <EmptyStateContent>
            <EmptyStateIcon>
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="38" stroke="#E0E0E0" strokeWidth="2" fill="#F8F9FA" />
                <path
                  d="M25 35L35 45L55 25"
                  stroke="#9E9E9E"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="40" cy="40" r="3" fill="#9E9E9E" />
              </svg>
            </EmptyStateIcon>
            <Box gap="16px" align="center">
              <GrooverText textKey="privateLessonsCheckoutPageEmptyStateTitle" language={selectedLanguage} />
              <Box style={{ maxWidth: '480px' }} align="center">
                <GrooverText textKey="privateLessonsCheckoutPageEmptyStateDescription" language={selectedLanguage} />
              </Box>
            </Box>
          </EmptyStateContent>
        </EmptyStateContainer>
      </CheckoutContainer>
    )
  }

  // Show loading screen while waiting for webhook to activate the order
  if (stripePostPaymentLoading) {
    return (
      <CheckoutContainer>
        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '48px' }}>
          <CircularProgress />
          <GrooverText textKey="checkoutPageProcessingPayment" language={selectedLanguage} />
        </Box>
      </CheckoutContainer>
    )
  }

  // Show success screen if payment was successful
  if (paymentSuccess) {
    return (
      <CheckoutContainer>
        {client?.logoFullUrl && (
          <SchoolBanner>
            <SchoolLogoWrapper>
              <SchoolLogo src={client.logoFullUrl} alt={client.name} />
            </SchoolLogoWrapper>
            <LanguageToggleWrapper>
              <PrivateLessonsLanguageToggle />
            </LanguageToggleWrapper>
          </SchoolBanner>
        )}

        <SuccessContent>
          <SuccessIcon>✓</SuccessIcon>
          <SuccessTitle>
            <GrooverText textKey="privateLessonsCheckoutPagePaymentSuccess" language={selectedLanguage} />
          </SuccessTitle>
          <SuccessMessage>
            <GrooverText textKey="privateLessonsCheckoutPagePaymentSuccessMessage" language={selectedLanguage} />
          </SuccessMessage>

          <PortalDescription>
            <GrooverText
              textKey="privateLessonsCheckoutOpenPortalDescription"
              language={selectedLanguage}
              style={{ color: '#666' }}
            />
          </PortalDescription>
          <PortalLinkButton href="/portal/login" target="_blank" rel="noopener noreferrer">
            <GrooverText
              textKey="privateLessonsCheckoutOpenPortalLink"
              language={selectedLanguage}
              style={{ color: 'white' }}
            />
          </PortalLinkButton>

          {orderId && (
            <QRCodeSection>
              <QRCodeTitle>
                <GrooverText textKey="privateLessonsCheckoutPageYourOrderId" language={selectedLanguage} />
              </QRCodeTitle>
              <QRCodeContainer>
                <QRCodeElement value={orderId} />
              </QRCodeContainer>
              <OrderIdText>{orderId}</OrderIdText>
            </QRCodeSection>
          )}

          {showAppDownloadSection && (
            <DownloadSection>
              <DownloadTitle>
                <GrooverText textKey="checkoutPageDownloadApp" language={selectedLanguage} />
              </DownloadTitle>
              <AppDescription>
                <GrooverText textKey="registrationStep5PaymentAppDescription" language={selectedLanguage} />
              </AppDescription>
              <DownloadButtonsContainer>
                <AppStoreButton
                  href="https://apps.apple.com/se/app/coursely-se/id6745610767?l=en-GB"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppStoreBadge
                    src={_getLanguageCode() === 'sv' ? swedishAppStoreBadge : englishAppStoreBadge}
                    alt="Download on the App Store"
                  />
                </AppStoreButton>
                <GooglePlayButton
                  href="https://play.google.com/store/apps/details?id=se.coursely.app&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GooglePlayBadge
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                  />
                </GooglePlayButton>
              </DownloadButtonsContainer>
            </DownloadSection>
          )}

          <ReturnButton onClick={handleCloseSuccessPage}>
            <GrooverText textKey="checkoutPageReturnToCheckout" language={selectedLanguage} />
          </ReturnButton>
        </SuccessContent>
      </CheckoutContainer>
    )
  }

  return (
    <CheckoutContainer>
      {client?.logoFullUrl && (
        <SchoolBanner>
          <SchoolLogoWrapper>
            <SchoolLogo src={client.logoFullUrl} alt={client.name} />
          </SchoolLogoWrapper>
          <LanguageToggleWrapper>
            <PrivateLessonsLanguageToggle />
          </LanguageToggleWrapper>
        </SchoolBanner>
      )}

      {/* Step Header */}
      <PrivateLessonsStepHeader
        currentStep={currentStep}
        onStepClick={goToStep}
        language={selectedLanguage}
        compact={isMobileLayout}
      />

      {/* Main Content */}
      <MainContent>
        {/* Left Column - Step Content */}
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}

        {/* Right Column - Order Summary */}
        {(!isMobileLayout || currentStep >= 3) && <RightColumn>{renderOrderSummary()}</RightColumn>}
      </MainContent>

      {/* Sticky CTAs for mobile (steps 1–3) */}
      {isMobileLayout && currentStep <= 3 && <StickyCtaBar>{renderNavigationButtons()}</StickyCtaBar>}

      {paymentError && (
        <ErrorAlert>
          <Alert severity="error" onClose={() => setPaymentError(null)}>
            {paymentError}
          </Alert>
        </ErrorAlert>
      )}

      <Dialog
        open={showSuccessModal}
        onClose={handleDismissSwishModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1
          }
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <SwishLogoContainer>
            <SwishLogoImage src={swishBigLogo} alt="Swish" />
          </SwishLogoContainer>

          {[SwishPaymentStatus.PENDING, SwishPaymentStatus.CREATED].includes(swishPaymentIntentStatus) && (
            <>
              <SwishModalTitle>
                <GrooverText textKey="checkoutPageOpenSwishTitle" language={selectedLanguage} />
              </SwishModalTitle>
              <SwishModalDescription>
                <GrooverText textKey="checkoutPageOpenSwishDescription" language={selectedLanguage} />
              </SwishModalDescription>
            </>
          )}

          {isPhoneDevice && (
            <OpenSwishButton onClick={handleOpenSwishApp}>
              <GrooverText
                textKey="checkoutPageOpenSwishButton"
                language={selectedLanguage}
                style={{ color: 'white' }}
              />
            </OpenSwishButton>
          )}

          {swishPaymentIntentStatus === SwishPaymentStatus.PAID && (
            <PaymentSuccessContent>
              <SuccessIcon>
                <CheckCircleIcon sx={{ fontSize: 40 }} />
              </SuccessIcon>
              <SuccessTitle>
                <GrooverText textKey="checkoutPagePaymentSuccessTitle" language={selectedLanguage} />
              </SuccessTitle>
              <SuccessMessage>
                <GrooverText textKey="checkoutPagePaymentSuccessMessage" language={selectedLanguage} />
              </SuccessMessage>
            </PaymentSuccessContent>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          {[SwishPaymentStatus.PENDING, SwishPaymentStatus.CREATED].includes(swishPaymentIntentStatus) && (
            <>
              <CloseButton
                onClick={handleCancelSwishPayment}
                style={{ width: '100%' }}
                disabled={paymentAbortInProgress}
              >
                {paymentAbortInProgress && <CircularProgress size={20} color="inherit" />}
                <GrooverText textKey={Texts.registrationCtactaAbort} />
              </CloseButton>
              <CircularProgress size={20} color="inherit" />
            </>
          )}
          {swishPaymentIntentStatus === SwishPaymentStatus.PAID && (
            <CloseButton onClick={handleCloseSuccessModal} style={{ width: '100%' }}>
              <GrooverText textKey="checkoutPageClose" language={selectedLanguage} />
            </CloseButton>
          )}
        </DialogActions>
      </Dialog>
    </CheckoutContainer>
  )
}

// Styled components
const CheckoutContainer = styled.div`
  min-height: 100vh;
  background-color: white;
`

const SchoolBanner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: white;
  border-bottom: 1px solid #e0e0e0;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 12px 14px;
    flex-direction: row;
    gap: 10px;
  }
`

const SchoolLogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`

const SchoolLogo = styled.img`
  max-height: 80px;
  max-width: 300px;
  object-fit: contain;

  @media (max-width: 768px) {
    max-height: 48px;
    max-width: 220px;
  }
`

const LanguageToggleWrapper = styled.div`
  flex-shrink: 0;
`

const PortalLinkButton = styled.a`
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 18px;
  border-radius: 12px;
  background: #7c3aed;
  text-decoration: none;
  font-weight: 700;
  min-width: min(520px, 100%);
  box-shadow: 0 6px 14px rgba(124, 58, 237, 0.25);

  &:hover {
    filter: brightness(0.98);
  }

  &:active {
    filter: brightness(0.95);
  }
`

const PortalDescription = styled.div`
  margin-top: 14px;
  text-align: center;
  max-width: 520px;
`

const MainContent = styled.div`
  display: flex;
  max-width: 1400px;
  min-width: 800px;
  margin: 0 auto;
  padding: 32px 8px;
  gap: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
    gap: 24px;
    min-width: unset;
    padding-bottom: 104px; /* room for sticky CTAs */
  }
`

const LeftColumn = styled.div`
  flex: 1;
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const RightColumn = styled.div`
  flex: 1;
  background: #f8f4ff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 20px;

  @media (max-width: 768px) {
    position: static;
    top: auto;
  }
`

const StickyCtaBar = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 50;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background: rgba(255, 255, 255, 0.98);
    border-top: 1px solid #e0e0e0;
    box-shadow: 0 -6px 16px rgba(0, 0, 0, 0.08);

    button {
      width: 100%;
    }
  }
`

const SectionTitle = styled.div`
  margin: 0 0 24px 0;
  font-size: 18px;
  font-weight: 600;
  color: #000;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

const PackageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`

const PackageOption = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border: 2px solid ${(props) => (props.$isSelected ? '#7c3aed' : '#e0e0e0')};
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${(props) => (props.$isSelected ? '#7c3aed' : '#ccc')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`

const PackageInfo = styled.div`
  flex: 1;
`

const PackageTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #000;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

const PopularTag = styled.span`
  background: #28a745;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

const PackageDescription = styled.p`
  margin: 8px 0 8px 0;
  color: #666;
  font-size: 0.9rem;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

const PackagePrice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #000;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

const RadioButton = styled.div<{ $isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border: 2px solid ${(props) => (props.$isSelected ? '#7c3aed' : '#ccc')};
  border-radius: 50%;
  background: ${(props) => (props.$isSelected ? '#7c3aed' : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
`

const InstructorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`

const FormField = styled.div`
  margin-bottom: 14px;
  width: 100%;
  box-sizing: border-box;

  > div:first-of-type {
    margin-bottom: 8px;
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #000;
  }
`

const PhoneInputWrapper = styled.div`
  /* Override TextFieldWithLabelForm background to white for this page */
  /* The TextFieldWithLabelForm uses StyledTextFieldForm with rgba(99, 20, 215, 0.05) background */
  /* We override it to white here */

  /* Target the TextFieldWithLabelForm structure */
  > div {
    /* TextFieldWithLabelFormWrapper */
    > div {
      /* TextFieldWithLabelForm Box wrapper */
      > div:last-child {
        /* Box containing StyledTextFieldForm */
        > div {
          /* StyledTextFieldForm - override background */
          background-color: white !important;
        }
      }
    }
  }

  /* Also ensure MUI input elements are white */
  .MuiOutlinedInput-root {
    background-color: white !important;
  }

  input {
    background-color: white !important;

    /* Match placeholder color to other inputs - darker gray to match visual appearance */
    &::placeholder {
      color: #666 !important;
      opacity: 1 !important;
    }
  }

  /* Override MUI placeholder color to match other inputs */
  .MuiInputBase-input::placeholder,
  .MuiOutlinedInput-input::placeholder,
  .css-1pzfmz2-MuiInputBase-input-MuiOutlinedInput-input::placeholder,
  .css-1sdw9wy-MuiInputBase-root-MuiOutlinedInput-root::placeholder {
    color: #666 !important;
    opacity: 1 !important;
  }
`

const StyledInput = styled.input<{ error?: boolean }>`
  width: 100%;
  max-width: 100%;
  padding: 12px 16px;
  border: 1px solid ${(props) => (props.error ? '#d32f2f' : '#ddd')};
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.error ? '#d32f2f' : '#7c3aed')};
  }

  &::placeholder {
    color: #999;
  }
`

const StyledTextArea = styled.textarea<{ error?: boolean }>`
  width: 100%;
  max-width: 100%;
  padding: 12px 16px;
  border: 1px solid ${(props) => (props.error ? '#d32f2f' : '#ddd')};
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.error ? '#d32f2f' : '#7c3aed')};
  }

  &::placeholder {
    color: #999;
  }
`

const BookingSection = styled.div`
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid #e0e0e0;
`

const BookingContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 16px;
`

const CalendarWrapper = styled.div`
  width: 100%;
`

const TimeSlotsWrapper = styled.div`
  width: 100%;
`

const SkipBookingCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  cursor: pointer;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #7c3aed;
  }

  label {
    font-size: 14px;
    color: #333;
    cursor: pointer;
    user-select: none;
    font-family: 'Plus Jakarta Sans', sans-serif;
  }
`

const NavigationButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`

const ContinueButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  background: #7c3aed;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Plus Jakarta Sans', sans-serif;

  &:hover:not(:disabled) {
    background: #6d28d9;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const BackButton = styled.button`
  padding: 14px 24px;
  background: white;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #ccc;
  }
`

const OrderSummarySection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e0e0e0;
`

const OrderItemsWrapper = styled.div`
  > div:last-child {
    border-bottom: none;
  }
`

const SwishPaymentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 400px;
  margin: 0 auto;
  padding: 24px 0;
`

const SwishLogoSingle = styled.img`
  height: 60px;
  width: auto;
  margin-bottom: 8px;
`

const OrderItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
`

const OrderLabel = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

const OrderValue = styled.div<{ $isTotal?: boolean }>`
  font-size: ${({ $isTotal }) => ($isTotal ? '18px' : '16px')};
  font-weight: ${({ $isTotal }) => ($isTotal ? 700 : 400)};
  color: ${({ $isTotal }) => ($isTotal ? '#7c3aed' : '#000')};
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
`

const OrderDivider = styled.div`
  height: 1px;
  background: #e0e0e0;
  margin: 16px 0;
`

const OrderTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 0 0;
`

const PayButton = styled.button`
  flex: 1;
  padding: 16px;
  background: #7c3aed;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #6d28d9;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const SwishIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`

const ErrorAlert = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  max-width: 500px;
  width: 90%;
`

const CloseButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: linear-gradient(45deg, #388e3c, #689f38);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #cccccc;
    color: #666666;
    cursor: not-allowed;
    transform: none;
  }
`

const SuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 40px 20px;
`

const QRCodeSection = styled.div`
  margin: 32px 0;
  text-align: center;
`

const QRCodeTitle = styled.div`
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 600;
  color: #000;
`

const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const OrderIdText = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #666;
  word-break: break-all;
  max-width: 300px;
  margin: 0 auto;
`

const DownloadSection = styled.div`
  margin: 32px 0;
  text-align: center;
  max-width: 500px;
`

const DownloadTitle = styled.div`
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 600;
  color: #000;
`

const AppDescription = styled.div`
  margin-bottom: 24px;
  line-height: 1.6;
  color: #666;
`

const DownloadButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    gap: 20px;
  }
`

const AppStoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  text-decoration: none;
  transition: opacity 0.2s ease;
  border: none;
  background: none;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`

const AppStoreBadge = styled.img`
  width: 180px;
  height: 60px;
`

const GooglePlayButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  text-decoration: none;
  transition: opacity 0.2s ease;
  border: none;
  background: none;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }
`

const GooglePlayBadge = styled.img`
  width: 180px;
  height: 60px;
`

const ReturnButton = styled.button`
  margin-top: 32px;
  padding: 12px 24px;
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;

  &:hover {
    background: linear-gradient(45deg, #388e3c, #689f38);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const SwishLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 24px;
`

const SwishLogoImage = styled.img`
  width: 120px;
  height: auto;
  display: block;
  margin: 0 auto;
`

const SwishModalTitle = styled.div`
  margin-bottom: 16px;
  font-size: 20px;
  font-weight: 600;
  color: #000;
`

const SwishModalDescription = styled.div`
  margin-bottom: 24px;
  line-height: 1.6;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  color: #666;
`

const OpenSwishButton = styled.button`
  padding: 16px 32px;
  background: #2c3e50;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  min-width: 200px;

  &:hover {
    background: #34495e;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const paymentFailedFlash = keyframes`
  0%, 100% { background: #ffebee; border-color: #f44336; }
  50% { background: #ffcdd2; border-color: #c62828; }
`

const PaymentFailedAlert = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  position: relative;
  animation: ${paymentFailedFlash} 1s ease-in-out 3;
`

const PaymentFailedIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
`

const PaymentFailedContent = styled.div`
  flex: 1;
`

const PaymentFailedTitle = styled.div`
  margin-bottom: 8px;
  font-weight: 600;
  color: #000;
`

const PaymentFailedMessage = styled.div`
  line-height: 1.5;
  color: #666;
`

const SupportEmailLink = styled.a`
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
    color: #1565c0;
  }

  &:active {
    color: #0d47a1;
  }
`

const CloseFailedButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
    color: #333;
  }
`

const EmptyStateContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 40px 20px;
`

const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 600px;
  width: 100%;
  background: white;
  border-radius: 16px;
  padding: 60px 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  color: #333;

  @media (max-width: 768px) {
    padding: 40px 24px;
    margin: 20px;
  }
`

const EmptyStateIcon = styled.div`
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PaymentSuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 0;
  gap: 16px;
`

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  box-shadow: 0 4px 16px rgba(76, 175, 80, 0.3);
  animation: successPulse 0.6s ease-in-out;
  font-size: 40px;
  font-weight: bold;

  @keyframes successPulse {
    0% {
      transform: scale(0.8);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`

const SuccessTitle = styled.div`
  margin-bottom: 8px;
  font-size: 24px;
  font-weight: 600;
  color: #000;
`

const SuccessMessage = styled.div`
  max-width: 400px;
  line-height: 1.5;
  opacity: 0.9;
  color: #666;
`

const PaymentMethodToggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`

const PaymentMethodButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px;
  border: 2px solid ${(props) => (props.$active ? '#7c3aed' : '#e0e0e0')};
  border-radius: 8px;
  background: ${(props) => (props.$active ? '#f3e8ff' : 'white')};
  font-weight: ${(props) => (props.$active ? '600' : '400')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

export default PrivateLessonsCheckoutPage
